import React, { Fragment } from 'react';
import FileSaver from 'file-saver';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Card } from 'react-bootstrap';
import { blankLink } from 'config';
import {
  IDocumentDownload,
  ILeadData,
  IPayloadForDocumentPdf,
  IPolicyObject,
  IProductDataForInvoice,
} from 'Services/widgets/interfaces';
import { useWidgetService } from 'Services/widget';
import downloadGMIconUrl from 'assets/bf-images/download_gm.svg';
import {
  billingFrom,
  billingTo,
  prepareAccountForInvoice,
  prepareDataForTariffGM,
  preparedDefaultGroupGM,
  prepareDefaulGroupAplication,
  prepareValuesForAplicationGM,
} from '../garantie-max-hooks';
import { JsonObject } from '@cover42/protobuf-util';
import { apiDateTimeFormat, defaultDateFormat, invoiceDateFormat } from 'App/ui-utils';
import { useLoadingSpinnerOnFullContainer } from '../../../utils/LoadingSpinner';
import { IProductDataGM } from '../GarantieMax';
import { useAppLogger } from 'Services/logger';
import { useAppAlertService } from 'App/components/utils/alerts/AppAlertService';
import { IPersonalDataStepGM } from '../steps/PersonalDataStep';
import { PdfDocumentType } from 'Services/widgets/enums';

export interface IViewDocumentDownloadProps {
  productCode: string;
  lead: ILeadData;
  productData: IProductDataGM;
}

export const documentList: IDocumentDownload[] = [
  {
    documentNameKey: 'bookingFunnel.policyApplication',
    documentType: PdfDocumentType.Policy,
  },
  {
    documentNameKey: 'Produktinformationsblatt',
    documentType: PdfDocumentType.Static,
    entityType: 'static_IPID',
  },
  {
    documentNameKey: 'Versicherungsbedingungen',
    documentType: PdfDocumentType.Static,
    entityType: 'static_AVB',
  },
  {
    documentNameKey: 'Kundeninformationspaket',
    documentType: PdfDocumentType.Static,
    entityType: 'static_INFO',
  },
];

export const ViewDocumentDownload: React.FC<IViewDocumentDownloadProps> = ( { productCode, lead, productData } ) => {
  const { t } = useTranslation( [ 'widgets', 'base' ] );
  const loadingOnFullContainer = useLoadingSpinnerOnFullContainer();
  const service = useWidgetService();
  const logger = useAppLogger();
  const { hideAlert, showAlert } = useAppAlertService();
  const [ isLoading, setIsLoading ] = React.useState( false );

  const downloadPdfHandler = React.useCallback(
    async ( nameFile: string, entityType: PdfDocumentType ): Promise<void> => {
      hideAlert();
      setIsLoading( true );
      const accountData = { ...lead.personalData, ...lead.paymentData } as JsonObject;
      const prepareAccountData = prepareAccountForInvoice( accountData );

      let payloadPdf: JsonObject = {
        account: prepareAccountData,
        date: dayjs().format( invoiceDateFormat ),
        city: 'Berlin',
        policyStartDate: dayjs( billingFrom ).format( defaultDateFormat ),
        policyEndDate: dayjs( billingTo ).format( defaultDateFormat ),
        dueDate: dayjs( billingTo ).format( defaultDateFormat ),
      };

      if ( entityType === PdfDocumentType.Invoice ) {
        const tariffData = { ...lead.subStep1TariffData };
        const prepareTariff = prepareDataForTariffGM(
          tariffData,
          lead.subStep1DeviceItems!,
          productData.insuredObjects,
          false,
        );

        const subStepData = { ...lead.subStep2TariffData };
        const preparedDefaultGroup = preparedDefaultGroupGM(
          subStepData,
          tariffData,
          lead.personalData as IPersonalDataStepGM,
          lead.subStep1DeviceItems!,
          productData.insuredObjects,
          false,
        );

        const policyObjectsData: IPolicyObject[] = [ ...prepareTariff, ...preparedDefaultGroup ];

        const payloadInvoice: IProductDataForInvoice = {
          policyObjects: policyObjectsData,
        };

        const invoiceResult = await service.getProductInvoice( productCode, payloadInvoice );
        payloadPdf = {
          ...payloadPdf,
          invoice: { ...invoiceResult } as unknown as JsonObject,
        };
      }

      let templateSlug = `demo-emil-${entityType}`;

      if ( entityType === PdfDocumentType.Policy ) {
        templateSlug = `${entityType}-application`;
        const preparePolicyData = prepareValuesForAplicationGM( lead, productData, t );
        const tariffObject = { ...lead.subStep1TariffData };
        const defaultData = { ...lead.subStep2TariffData };
        const preparedGroup = preparedDefaultGroupGM(
          defaultData,
          tariffObject,
          lead.personalData as IPersonalDataStepGM,
          lead.subStep1DeviceItems!,
          productData.insuredObjects,
          false,
        );

        const defaultGroupData = prepareDefaulGroupAplication(
          preparedGroup,
          productData,
          t,
        );

        const formattedTimeline = {
          from: dayjs( billingFrom ).format( apiDateTimeFormat ),
          to: dayjs( billingTo ).format( apiDateTimeFormat ),
          policyObjects: [ ...preparePolicyData ],
          defaultPolicyObject: { ...defaultGroupData },
        };

        const prepareTariff = prepareDataForTariffGM(
          tariffObject,
          lead.subStep1DeviceItems!,
          productData.insuredObjects,
          false,
        );

        const policyObjectsData: IPolicyObject[] = [ ...prepareTariff, ...preparedGroup ];

        const payloadInvoice: IProductDataForInvoice = {
          policyObjects: policyObjectsData,
          billingIntervalFrom: billingFrom,
          billingIntervalTo: billingTo,
        };

        const invoiceResult = await service.getProductInvoice( productCode, payloadInvoice );

        payloadPdf = {
          ...payloadPdf,
          policy: {
            productName: productData.productName,
            createdAt: dayjs().format( apiDateTimeFormat ),
          },
          formattedTimeline: { ...formattedTimeline } as unknown as JsonObject,
          invoice: { ...invoiceResult } as unknown as JsonObject,
        };
      }

      const payloadForDocumentPdf: IPayloadForDocumentPdf = {
        templateSlug,
        entityType,
        description: `Pdf document ${entityType}`,
        requester: 'publicapi',
        payload: payloadPdf,
      };

      return service.downloadPdfDocument( payloadForDocumentPdf )
        .then( ( res ) => {
          const blob = new Blob( [ res ], {
            type: 'application/pdf',
          } );
          FileSaver.saveAs( blob, `${nameFile}.pdf` );
          setIsLoading( false );
        } )
        .catch( ( error ) => {
          logger.error( error );
          showAlert( {
            message: t( 'base:forms.messages.error' ),
            type: 'danger',
          } );
          setIsLoading( false );
        } );
    }, [ hideAlert, lead, logger, productCode, productData, service, showAlert, t ] );


  const downloadStaticPdfHandler = React.useCallback(
    async ( nameFile: string, entityType: string ): Promise<void> => {
      hideAlert();

      const document = productData.staticDocuments.find( ( d ) => d.entityType === entityType );

      if( !document ) {
        showAlert( {
          message: t( 'base:forms.messages.error' ),
          type: 'danger',
        } );

        return;
      }

      setIsLoading( true );

      return service.downloadStaticPdfDocument( document.code )
        .then( ( res ) => {
          const blob = new Blob( [ res ], {
            type: 'application/pdf',
          } );
          FileSaver.saveAs( blob, `${nameFile}.pdf` );
          setIsLoading( false );
        } )
        .catch( ( error ) => {
          logger.error( error );
          showAlert( {
            message: t( 'base:forms.messages.error' ),
            type: 'danger',
          } );
          setIsLoading( false );
        } );
    }, [ hideAlert, logger, productData.staticDocuments, service, showAlert, t ] );

  return (
    <Fragment>
      <Card className="bg-none">
        <Card.Body>
          { isLoading && loadingOnFullContainer }
          <h3>{ t( 'bookingFunnel.summary.downloadDocs' ) }</h3>
          <hr className="mt-3 mb-3"></hr>
          <div className="text-left">
            { documentList.map( ( d, idx ) => {
              return (
                <Fragment key={ idx }>
                  { d.entityType ? (
                    <div className="download-section">
                      <a
                        href={ blankLink }
                        onClick={ ( e ) => {
                          e.preventDefault();
                          downloadStaticPdfHandler( t( `${d.documentNameKey}` ), d.entityType! );
                        } }
                      >{ t( `${d.documentNameKey}` ) }
                        <img src={ downloadGMIconUrl } className="float-right" alt="download" />
                      </a>
                    </div>
                  ) : (
                    <div className="download-section">
                      <a
                        href={ blankLink }
                        onClick={ ( e ) => {
                          e.preventDefault();
                          downloadPdfHandler( t( `${d.documentNameKey}` ), d.documentType );
                        } }
                      >{ t( `${d.documentNameKey}` ) }
                        <img src={ downloadGMIconUrl } className="float-right" alt="download" />
                      </a>
                    </div>
                  ) }
                </Fragment>
              );
            } ) }
          </div>
        </Card.Body>
      </Card>
    </Fragment>
  );
};
